import React from 'react';
import { FaWaveSquare, FaChartArea, FaBolt, FaCompressArrowsAlt } from 'react-icons/fa';

const REGIME_STYLES = {
  LOW_VOL: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: FaCompressArrowsAlt, label: 'Low Volatility' },
  NORMAL: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', icon: FaChartArea, label: 'Normal' },
  HIGH_VOL: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: FaWaveSquare, label: 'High Volatility' },
  EXPANSION: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: FaBolt, label: 'Expansion' },
};

export default function RegimeIndicator({ regime, expectedMove, moveExhausted, indicators }) {
  if (!regime) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
        <div className="text-gray-400 text-sm">Regime data unavailable</div>
      </div>
    );
  }

  const regimeKey = regime.regime || 'NORMAL';
  const style = REGIME_STYLES[regimeKey] || REGIME_STYLES.NORMAL;
  const Icon = style.icon;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${style.bg} ${style.border} border`}>
            <Icon className={style.text} />
          </div>
          <div>
            <div className={`text-sm font-semibold ${style.text}`}>{style.label}</div>
            <div className="text-[10px] text-gray-400">Volatility Regime</div>
          </div>
        </div>
        {moveExhausted && (
          <span className="px-2 py-1 rounded-full text-[10px] font-medium bg-amber-100 text-amber-600">
            Move Exhausted
          </span>
        )}
      </div>

      {/* Regime details */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        {regime.atr > 0 && (
          <MiniStat label="ATR" value={regime.atr?.toFixed(1)} />
        )}
        {regime.atr_percentile > 0 && (
          <MiniStat label="ATR %ile" value={`${regime.atr_percentile?.toFixed(0)}%`} />
        )}
        {regime.range_compression > 0 && (
          <MiniStat label="Range" value={`${regime.range_compression?.toFixed(2)}x`} />
        )}
      </div>

      {/* Expected move */}
      {expectedMove && expectedMove.expected_move > 0 && (
        <div className="p-2 rounded bg-gray-50 border border-gray-200">
          <div className="text-[10px] text-gray-400 uppercase mb-1">Expected Move</div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-red-600">{expectedMove.lower_bound?.toFixed(0)}</span>
            <span className="text-sm font-mono text-gray-900">
              ±{expectedMove.expected_move?.toFixed(0)} pts
              <span className="text-gray-400 text-xs ml-1">({expectedMove.expected_move_pct?.toFixed(2)}%)</span>
            </span>
            <span className="text-xs text-emerald-600">{expectedMove.upper_bound?.toFixed(0)}</span>
          </div>
        </div>
      )}

      {/* Key indicators */}
      {indicators && (
        <div className="flex items-center gap-3 mt-3 text-[10px] text-gray-400">
          {indicators.ema9 && <span>EMA9: <span className="text-gray-700">{indicators.ema9?.toFixed(0)}</span></span>}
          {indicators.ema21 && <span>EMA21: <span className="text-gray-700">{indicators.ema21?.toFixed(0)}</span></span>}
          {indicators.rsi && <span>RSI: <span className="text-gray-700">{indicators.rsi?.toFixed(0)}</span></span>}
          {indicators.vwap && <span>VWAP: <span className="text-gray-700">{indicators.vwap?.toFixed(0)}</span></span>}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-[10px] text-gray-400">{label}</div>
      <div className="text-xs font-mono text-gray-900">{value}</div>
    </div>
  );
}
